/**
 * The outcome poster.
 *
 * Drawn to a 1200×630 canvas in the same print language as the interface, so what gets
 * posted looks like the thing it came from. It carries the outcome, the day, the decisive
 * moment, the participation-versus-support gap over the whole run, the seed, and the URL —
 * everything a reader needs either to argue with it or to reproduce it exactly.
 *
 * Canvas rather than an SVG-to-image conversion: `drawImage` of a serialised SVG taints
 * the canvas in some browsers and silently breaks the clipboard write, and the poster is
 * different enough from the on-screen graph that sharing code would have meant compromises
 * in both.
 */

import type { RunLog } from 'walkout-engine';

const W = 1200;
const H = 630;

const PAPER = '#f1e9d9';
const PAPER_DEEP = '#d3c7ae';
const BLACK = '#17130f';
const RED = '#d8402a';
const BLUE = '#1f3fd4';

const DISPLAY =
  "'Haettenschweiler', 'Arial Narrow Bold', 'Helvetica Neue Condensed', 'Roboto Condensed', Impact, 'Arial Narrow', sans-serif";
const BODY = "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const MONO = "'SF Mono', 'Cascadia Mono', 'Roboto Mono', Menlo, Consolas, monospace";

const HEADLINE: Record<RunLog['outcome']['kind'], string> = {
  conceded: 'THE DEMANDS WERE MET',
  negotiated: 'A PARTIAL SETTLEMENT',
  stalemate: 'NEITHER SIDE BROKE',
  collapse: 'THE CAMPAIGN COLLAPSED',
  'violent-escalation': 'IT TURNED VIOLENT',
};

const ACCENT: Record<RunLog['outcome']['kind'], string> = {
  conceded: RED,
  negotiated: BLUE,
  stalemate: BLACK,
  collapse: BLACK,
  'violent-escalation': '#4a1f6b',
};

function pct(value: number): string {
  const percent = value * 100;
  if (percent >= 10) return `${Math.round(percent)}%`;
  return `${percent.toFixed(1)}%`;
}

/** Wrap text to a pixel width, returning at most `maxLines` lines. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length === maxLines) return lines;
    } else {
      current = candidate;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines;
}

/** Paper grain, drawn as sparse noise. Deterministic given the run, so the poster is too. */
function drawGrain(ctx: CanvasRenderingContext2D, seed: number): void {
  let state = seed >>> 0 || 1;
  const next = (): number => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = BLACK;
  for (let i = 0; i < 14000; i++) {
    ctx.fillRect(Math.floor(next() * W), Math.floor(next() * H), 1, 1);
  }
  ctx.restore();
}

/**
 * The gap chart: participation and private support across the whole run, with the space
 * between them left empty. It is the one chart worth putting on a poster, because the
 * shape of that space is the finding.
 */
function drawGapChart(
  ctx: CanvasRenderingContext2D,
  run: RunLog,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const days = run.days;
  if (days.length < 2) return;

  const maxValue = Math.max(
    0.1,
    ...days.map((d) => Math.max(d.privateSupport, d.participation)),
  );
  const px = (index: number): number => x + (index / (days.length - 1)) * width;
  const py = (value: number): number => y + height - (value / maxValue) * height;

  // The void between the two lines.
  ctx.beginPath();
  ctx.moveTo(px(0), py(days[0]?.privateSupport ?? 0));
  days.forEach((d, i) => ctx.lineTo(px(i), py(d.privateSupport)));
  for (let i = days.length - 1; i >= 0; i--) ctx.lineTo(px(i), py(days[i]?.participation ?? 0));
  ctx.closePath();
  ctx.fillStyle = PAPER_DEEP;
  ctx.fill();

  // Support.
  ctx.beginPath();
  days.forEach((d, i) => (i === 0 ? ctx.moveTo(px(i), py(d.privateSupport)) : ctx.lineTo(px(i), py(d.privateSupport))));
  ctx.strokeStyle = BLUE;
  ctx.lineWidth = 4;
  ctx.stroke();

  // Participation, filled to the baseline: what people actually did.
  ctx.beginPath();
  ctx.moveTo(px(0), y + height);
  days.forEach((d, i) => ctx.lineTo(px(i), py(d.participation)));
  ctx.lineTo(px(days.length - 1), y + height);
  ctx.closePath();
  ctx.fillStyle = RED;
  ctx.fill();

  ctx.strokeStyle = BLACK;
  ctx.lineWidth = 3;
  ctx.strokeRect(x, y, width, height);

  ctx.font = `600 15px ${BODY}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = BLUE;
  ctx.fillText('private support', x + 10, y + 22);
  // Set in paper, not red: this label sits on top of the red fill, and red on red is
  // invisible — which is exactly how it shipped the first time.
  ctx.fillStyle = PAPER;
  ctx.fillText('out on strike', x + 10, y + height - 12);
}

export function drawPoster(canvas: HTMLCanvasElement, run: RunLog, url: string): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const ratio = window.devicePixelRatio || 1;
  canvas.width = W * ratio;
  canvas.height = H * ratio;
  ctx.scale(ratio, ratio);

  const accent = ACCENT[run.outcome.kind];

  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, W, H);

  // --- Masthead ------------------------------------------------------------
  ctx.fillStyle = BLACK;
  ctx.fillRect(0, 0, W, 76);
  ctx.fillStyle = PAPER;
  ctx.font = `34px ${DISPLAY}`;
  ctx.textAlign = 'left';
  ctx.fillText('WALKOUT', 44, 52);
  ctx.font = `17px ${MONO}`;
  ctx.fillStyle = PAPER_DEEP;
  const scenarioName = run.config.scenarioId.replace(/-/g, ' ').toUpperCase();
  ctx.fillText(scenarioName, 200, 50);
  ctx.textAlign = 'right';
  ctx.fillText(`SEED ${run.config.seed}`, W - 44, 50);

  // --- Outcome -------------------------------------------------------------
  ctx.textAlign = 'left';
  ctx.fillStyle = accent;
  ctx.font = `76px ${DISPLAY}`;
  const headlineLines = wrapText(ctx, HEADLINE[run.outcome.kind], 640, 2);
  headlineLines.forEach((line, i) => ctx.fillText(line, 44, 152 + i * 66));

  ctx.fillStyle = BLACK;
  ctx.font = `22px ${MONO}`;
  ctx.fillText(`ON DAY ${run.outcome.day}`, 44, 152 + headlineLines.length * 66 + 4);

  // --- Why -----------------------------------------------------------------
  ctx.font = `17px ${BODY}`;
  let textY = 152 + headlineLines.length * 66 + 46;
  for (const reason of run.outcome.reasons.slice(0, 3)) {
    const lines = wrapText(ctx, reason, 620, 3);
    ctx.fillStyle = accent;
    ctx.fillRect(44, textY - 12, 16, 3);
    ctx.fillStyle = BLACK;
    for (const line of lines) {
      ctx.fillText(line, 70, textY);
      textY += 23;
    }
    textY += 10;
    if (textY > 500) break;
  }

  // --- The gap -------------------------------------------------------------
  const chartX = 720;
  const chartY = 118;
  const chartW = 436;
  const chartH = 218;
  drawGapChart(ctx, run, chartX, chartY, chartW, chartH);

  ctx.textAlign = 'left';
  ctx.fillStyle = BLACK;
  ctx.font = `13px ${MONO}`;
  ctx.fillText('WHAT THEY WANTED, AND WHAT THEY DID', chartX, chartY - 14);

  // The headline number: the widest gap the run ever reached.
  ctx.font = `112px ${DISPLAY}`;
  ctx.fillStyle = PAPER;
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = 3;
  const gapText = pct(run.widestGap.value);
  ctx.fillText(gapText, chartX, chartY + chartH + 108);
  ctx.strokeText(gapText, chartX, chartY + chartH + 108);

  ctx.font = `600 17px ${BODY}`;
  ctx.fillStyle = BLACK;
  const gapLabel = wrapText(
    ctx,
    `of the workforce backed this and never acted on it — peak gap, day ${run.widestGap.day}`,
    chartW - 10,
    3,
  );
  gapLabel.forEach((line, i) =>
    ctx.fillText(line, chartX, chartY + chartH + 136 + i * 22),
  );

  // --- Footer --------------------------------------------------------------
  ctx.fillStyle = BLACK;
  ctx.fillRect(0, H - 58, W, 58);
  ctx.fillStyle = PAPER;
  ctx.font = `15px ${MONO}`;
  ctx.textAlign = 'left';
  ctx.fillText(`PEAK ${pct(run.peakParticipation.value)} OUT ON DAY ${run.peakParticipation.day}`, 44, H - 22);
  ctx.textAlign = 'right';
  const shown = url.length > 74 ? `${url.slice(0, 71)}…` : url;
  ctx.fillText(shown, W - 44, H - 22);

  drawGrain(ctx, run.config.seed);
}

export function posterFilename(run: RunLog): string {
  return `walkout-${run.config.scenarioId}-seed${run.config.seed}-${run.outcome.kind}.png`;
}

export async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The browser would not produce an image from the poster.'));
    }, 'image/png');
  });
}
